import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { RefreshCw, TrendingUp, BarChart2, Activity, Award, Clock, Play, Square, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';

const getBaseUrl = () => {
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const url = isLocal ? 'http://localhost:5000' : window.location.origin;
    console.log(`[BaseURL] Current: ${window.location.hostname}, Chosen: ${url}`);
    return url;
};

const Page = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 64px);
  background: #141414;
  color: #fff;
  font-family: 'Inter', sans-serif;
  overflow: hidden;
`;

const Header = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid #2a2a2a;
  background: #1f1f1f;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TitleArea = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RefreshButton = styled.button`
  background: #2a2a2a;
  border: 1px solid #444;
  color: #fff;
  padding: 8px 12px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-size: 13px;
  &:hover { background: #333; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const HistoryLink = styled(Link)`
  text-decoration: none;
  background: #1a365d;
  color: #60a5fa;
  border: 1px solid #1e40af;
  padding: 8px 12px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  margin-right: 12px;
  &:hover { background: #1e3a8a; }
`;

const Layout = styled.div`
  display: flex;
  flex: 1;
  padding: 16px;
  gap: 16px;
  overflow: hidden;
`;

// Left: Empty for future sector
const LeftPanel = styled.div`
  width: 250px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

// Middle: Main Score Board
const CenterPanel = styled.div`
  flex: 1;
  background: #1a1a1a;
  border: 1px solid #2a2a2a;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

// Right: Details/News
const RightPanel = styled.div`
  width: 320px;
  background: #1a1a1a;
  border: 1px solid #2a2a2a;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const PanelHeader = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid #2a2a2a;
  font-weight: 600;
  font-size: 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #1f1f1f;
`;

const PanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  th, td {
    padding: 12px 16px;
    text-align: right;
    border-bottom: 1px solid #2a2a2a;
    font-size: 13px;
    vertical-align: middle;
  }
  th {
    color: #888;
    font-weight: 500;
    position: sticky;
    top: 0;
    background: #1a1a1a;
    z-index: 10;
  }
  th:first-child, td:first-child { text-align: left; }
  tr { cursor: pointer; }
  tr:hover { background: #222; }
  tr.active { background: #1e3a8a; }
`;

const ItemName = styled.div`
  font-weight: 600;
  color: #fff;
  font-size: 14px;
`;

const ItemCode = styled.div`
  color: #666;
  font-size: 11px;
`;

const ScoreBarBg = styled.div`
  width: 120px;
  height: 8px;
  background: #333;
  border-radius: 4px;
  overflow: hidden;
  margin-top: 4px;
`;

const ScoreBarFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #e50914, #f59e0b);
  width: ${p => p.$width}%;
  border-radius: 4px;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #666;
  font-size: 14px;
  padding: 20px;
  text-align: center;
`;

const SchedulerCard = styled.div`
  background: #1a1a1a;
  border: 1px solid #2a2a2a;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SchedulerTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  margin: 0;
  color: #ef4444;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 13px;
  color: #888;
`;

const Input = styled.input`
  background: #111;
  border: 1px solid #333;
  color: #fff;
  padding: 10px;
  border-radius: 6px;
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;
  &:focus { outline: none; border-color: #ef4444; }
`;

const TimeGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const ActionButton = styled.button`
  background: ${p => p.$stop ? '#333' : '#e50914'};
  color: #fff;
  border: none;
  padding: 12px;
  border-radius: 8px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 8px;
  transition: all 0.2s;
  &:hover { opacity: 0.9; transform: translateY(-1px); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const LogArea = styled.div`
  background: #111;
  border-radius: 6px;
  padding: 8px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  color: #666;
  max-height: 100px;
  overflow-y: auto;
  border: 1px solid #222;
`;

export default function MoneyFlowPage() {
    const [loading, setLoading] = useState(false);
    const [rankings, setRankings] = useState([]);
    const [themes, setThemes] = useState([]);
    const [selectedStock, setSelectedStock] = useState(null);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);

    // Scheduler State
    const [schedConfig, setSchedConfig] = useState({
        interval: 5,
        startTime: '00:00',
        endTime: '23:59'
    });
    const [schedStatus, setSchedStatus] = useState({
        isRunning: false,
        logs: []
    });
    const [isSaving, setIsSaving] = useState(false);

    const fetchSchedulerStatus = async (isInitial = false) => {
        try {
            const res = await axios.get(`${getBaseUrl()}/api/moneyflow/scheduler/status`);
            setSchedStatus({
                isRunning: res.data.isRunning,
                logs: res.data.logs
            });
            // Only overwrite UI config if it's initial load or if the scheduler is running (to stay synced)
            // If user is editing while stopped, don't overwrite.
            if (isInitial || res.data.isRunning) {
                setSchedConfig(res.data.config);
            }
        } catch (err) {
            console.error('Failed to fetch scheduler status:', err);
        }
    };

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const [rankRes, themeRes] = await Promise.all([
                axios.get(`${getBaseUrl()}/api/moneyflow/rankings?type=score`),
                axios.get(`${getBaseUrl()}/api/moneyflow/themes`)
            ]);
            
            const data = rankRes.data.data || [];
            setRankings(data);
            setThemes(themeRes.data || []);
            
            if (data.length > 0 && !selectedStock) {
                setSelectedStock(data[0]); // Select first by default
            }
            setLastUpdated(new Date());
        } catch (err) {
            console.error('Failed to fetch money flow data:', err);
            setError('실시간 통합 데이터를 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        fetchSchedulerStatus(true);
    }, []);

    useEffect(() => {
        const delay_ms = Math.max(1, schedConfig.interval || 5) * 60 * 1000;
        const interval = setInterval(() => {
            fetchData();
            fetchSchedulerStatus();
        }, delay_ms);
        return () => clearInterval(interval);
    }, [schedConfig.interval]);

    const toggleScheduler = async () => {
        setIsSaving(true);
        try {
            if (schedStatus.isRunning) {
                await axios.post(`${getBaseUrl()}/api/moneyflow/scheduler/stop`);
            } else {
                // When starting, sync the current UI config to backend
                await axios.post(`${getBaseUrl()}/api/moneyflow/scheduler/config`, {
                    ...schedConfig,
                    enabled: true
                });
            }
            await fetchSchedulerStatus();
        } catch (err) {
            console.error('Scheduler toggle failed:', err);
            alert('스케쥴러 제어에 실패했습니다.');
        } finally {
            setIsSaving(false);
        }
    };

    const updateConfig = async () => {
        setIsSaving(true);
        try {
            await axios.post(`${getBaseUrl()}/api/moneyflow/scheduler/config`, schedConfig);
            alert('설정이 저장되었습니다.');
            await fetchSchedulerStatus();
        } catch (err) {
            alert('설정 저장에 실패했습니다.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleTimeChange = (e, field) => {
        let val = e.target.value.replace(/[^0-9:]/g, '').substring(0, 5);
        if (val.length === 2 && !val.includes(':') && (e.nativeEvent && e.nativeEvent.inputType !== 'deleteContentBackward')) {
            val += ':';
        }
        setSchedConfig({...schedConfig, [field]: val});
    };

    // Helper color
    const getChangeColor = (change) => {
        if (change > 0) return '#ef4444';
        if (change < 0) return '#3b82f6';
        return '#fff';
    };

    return (
        <Page>
            <Header>
                <TitleArea>
                    <Title><Activity color="#e50914" /> 자금 흐름 통합 대시보드</Title>
                    {lastUpdated && (
                        <span style={{ fontSize: '12px', color: '#888', marginLeft: '8px' }}>
                            마지막 업데이트: {lastUpdated.toLocaleTimeString()}
                        </span>
                    )}
                </TitleArea>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <HistoryLink to="/labs/moneyflow/history">
                        <BarChart2 size={16} /> 스냅샷 기록
                    </HistoryLink>
                    <RefreshButton onClick={fetchData} disabled={loading}>
                        <RefreshCw size={14} className={loading ? "spin" : ""} /> 
                        {loading ? '갱신 중...' : '새로고침'}
                    </RefreshButton>
                </div>
            </Header>

            {error && <div style={{ padding: '10px 16px', background: '#7f1d1d', color: '#fca5a5', fontSize: '13px' }}>{error}</div>}

            <Layout>
                {/* Left Panel: Sectors & Themes & Scheduler */}
                <LeftPanel>
                    {/* Scheduler Settings */}
                    <SchedulerCard>
                        <SchedulerTitle><Clock size={16} /> 스케쥴러 설정</SchedulerTitle>
                        
                        <FormGroup>
                            <Label>작업 간격 (분)</Label>
                            <Input 
                                type="number" 
                                value={schedConfig.interval}
                                onChange={(e) => setSchedConfig({...schedConfig, interval: parseInt(e.target.value)})}
                                placeholder="5"
                            />
                        </FormGroup>

                        <TimeGrid>
                            <FormGroup>
                                <Label>시작 시간</Label>
                                <Input 
                                    type="text"
                                    placeholder="HH:mm"
                                    maxLength="5"
                                    value={schedConfig.startTime}
                                    onChange={(e) => handleTimeChange(e, 'startTime')}
                                />
                            </FormGroup>
                            <FormGroup>
                                <Label>종료 시간</Label>
                                <Input 
                                    type="text"
                                    placeholder="HH:mm"
                                    maxLength="5"
                                    value={schedConfig.endTime}
                                    onChange={(e) => handleTimeChange(e, 'endTime')}
                                />
                            </FormGroup>
                        </TimeGrid>

                        <ActionButton 
                            onClick={toggleScheduler} 
                            disabled={isSaving}
                            $stop={schedStatus.isRunning}
                        >
                            {schedStatus.isRunning ? <Square size={14} /> : <Play size={14} />}
                            {schedStatus.isRunning ? '작업 중지' : '작업 시작'}
                        </ActionButton>

                        {schedStatus.logs.length > 0 && (
                            <LogArea>
                                {schedStatus.logs.map((log, lIdx) => (
                                    <div key={lIdx}>{log}</div>
                                ))}
                            </LogArea>
                        )}
                    </SchedulerCard>

                    <div style={{ background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: '8px', flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        <PanelHeader>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <TrendingUp size={16} color="#22c55e"/> 주도 테마 Top 10
                            </div>
                        </PanelHeader>
                        <PanelContent style={{ padding: '8px' }}>
                            {themes.length === 0 ? (
                                <EmptyState>데이터 수집 중...</EmptyState>
                            ) : (
                                themes.map((theme, i) => (
                                    <div key={i} style={{ padding: '12px', borderBottom: '1px solid #2a2a2a', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div style={{ fontSize: '14px', fontWeight: 600, color: '#fff', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <span style={{ color: '#888', fontSize: '12px', width: '16px' }}>{i + 1}</span>
                                                <span style={{ 
                                                    whiteSpace: 'nowrap', 
                                                    overflow: 'hidden', 
                                                    textOverflow: 'ellipsis', 
                                                    maxWidth: '120px' 
                                                }} title={theme.name}>{theme.name}</span>
                                            </div>
                                            <div style={{ fontWeight: 700, fontSize: '14px', color: getChangeColor(theme.change) }}>
                                                {theme.change > 0 ? '+' : ''}{theme.change.toFixed(2)}%
                                            </div>
                                        </div>
                                        {theme.leaders && theme.leaders.length > 0 && (
                                            <div style={{ fontSize: '11px', color: '#888', display: 'flex', gap: '4px', flexWrap: 'wrap', paddingLeft: '22px' }}>
                                                대장주: {theme.leaders.slice(0, 2).map((leader, lIdx) => (
                                                    <span key={lIdx} style={{ background: '#333', padding: '2px 6px', borderRadius: '4px', color: '#ddd' }}>
                                                        {leader}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))
                            )}
                        </PanelContent>
                    </div>
                </LeftPanel>

                {/* Center Panel: Score Rankings */}
                <CenterPanel>
                    <PanelHeader>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Award size={16} color="#f59e0b"/> Money Flow 통합 랭킹 Top 50
                        </div>
                    </PanelHeader>
                    <PanelContent>
                        {rankings.length === 0 ? (
                            <EmptyState>{loading ? '데이터 수집 중...' : '조건에 맞는 종목이 없습니다.'}</EmptyState>
                        ) : (
                            <Table>
                                <thead>
                                    <tr>
                                        <th style={{ width: '60px' }}>Rank</th>
                                        <th>종목명</th>
                                        <th>현재가 (대비)</th>
                                        <th style={{ width: '150px' }}>통합 점수 (100)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rankings.map((item, i) => (
                                        <tr 
                                            key={item.code} 
                                            className={selectedStock?.code === item.code ? 'active' : ''}
                                            onClick={() => setSelectedStock(item)}
                                        >
                                            <td style={{ color: '#888', fontWeight: 'bold' }}>{i + 1}</td>
                                            <td>
                                                <ItemName>{item.name}</ItemName>
                                                <ItemCode>{item.code}</ItemCode>
                                            </td>
                                            <td>
                                                <div style={{ fontWeight: 600, color: getChangeColor(item.change) }}>
                                                    {item.price.toLocaleString()}
                                                </div>
                                                <div style={{ fontSize: '11px', color: getChangeColor(item.change) }}>
                                                    {item.change > 0 ? '+' : ''}{item.change.toLocaleString()}
                                                </div>
                                            </td>
                                            <td>
                                                <div style={{ fontWeight: 700, fontSize: '15px', color: '#f59e0b' }}>
                                                    {item.score.toFixed(1)} <span style={{fontSize: '11px', color:'#888'}}>점</span>
                                                </div>
                                                <ScoreBarBg>
                                                    <ScoreBarFill $width={Math.min(item.score, 100)} />
                                                </ScoreBarBg>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </PanelContent>
                </CenterPanel>

                {/* Right Panel: Selected Stock Breakdown */}
                <RightPanel>
                    <PanelHeader>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <TrendingUp size={16} color="#3b82f6"/> 상세 점수 기여도
                        </div>
                    </PanelHeader>
                    <PanelContent>
                        {selectedStock ? (
                            <div style={{ padding: '20px' }}>
                                <h3 style={{ margin: '0 0 4px 0', color: '#fff' }}>{selectedStock.name}</h3>
                                <div style={{ color: '#888', fontSize: '13px', marginBottom: '24px' }}>{selectedStock.code}</div>

                                <div style={{ background: '#222', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
                                    <div style={{ fontSize: '12px', color: '#888', marginBottom: '8px' }}>최종 MoneyFlow Score</div>
                                    <div style={{ fontSize: '28px', fontWeight: 800, color: '#f59e0b' }}>{selectedStock.score.toFixed(1)}</div>
                                </div>

                                <h4 style={{ color: '#ccc', fontSize: '13px', borderBottom: '1px solid #333', paddingBottom: '8px', marginBottom: '16px' }}>
                                    평가 요소별 랭킹 (Top 100 기준)
                                </h4>
                                
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '14px' }}>
                                    <span style={{ color: '#888' }}>거래대금 (40%)</span>
                                    <span style={{ fontWeight: 600, color: selectedStock.details?.volRank > 0 ? '#fff' : '#444' }}>
                                        {selectedStock.details?.volRank > 0 ? `${selectedStock.details.volRank}위` : '권외'}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '14px' }}>
                                    <span style={{ color: '#888' }}>체결강도 (30%)</span>
                                    <span style={{ fontWeight: 600, color: selectedStock.details?.strRank > 0 ? '#fff' : '#444' }}>
                                        {selectedStock.details?.strRank > 0 ? `${selectedStock.details.strRank}위` : '권외'}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '14px' }}>
                                    <span style={{ color: '#888' }}>프로그램순매수 (30%)</span>
                                    <span style={{ fontWeight: 600, color: selectedStock.details?.prgRank > 0 ? '#fff' : '#444' }}>
                                        {selectedStock.details?.prgRank > 0 ? `${selectedStock.details.prgRank}위` : '권외'}
                                    </span>
                                </div>
                                
                                {selectedStock.details?.newsBonus > 0 && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', fontSize: '14px', background: 'rgba(245, 158, 11, 0.1)', padding: '8px', borderRadius: '4px' }}>
                                        <span style={{ color: '#f59e0b', fontWeight: 600 }}>정성적 호재 가점</span>
                                        <span style={{ fontWeight: 700, color: '#f59e0b' }}>
                                            +{selectedStock.details.newsBonus}점
                                        </span>
                                    </div>
                                )}

                                <div style={{ marginTop: '30px', padding: '16px', background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.2)', borderRadius: '8px', color: '#93c5fd', fontSize: '12px', lineHeight: 1.5 }}>
                                    <strong>스코어 안내:</strong><br />
                                    각 지표의 시장 내 실시간 순위(Top 100)를 기반으로 0~100점 점수를 환산 후 가중치를 합산합니다. 특징주 뉴스에서 호재 키워드 발견 시 정성적 가점(+15)이 부여됩니다. 랭킹이 높을수록 시장 주도 수급일 확률이 높습니다.
                                </div>
                            </div>
                        ) : (
                            <EmptyState>좌측 랭킹에서 종목을 선택하세요.</EmptyState>
                        )}
                    </PanelContent>
                </RightPanel>
            </Layout>
        </Page>
    );
}
